import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Search, Star, Check, EyeOff, Eye, Trash2, MessageSquare, ShieldCheck, X, ImageIcon } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { adminAPI } from '@/utils/adminApi';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const Stars = ({ value }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(n => (
      <Star key={n} className={`w-4 h-4 ${n <= value ? 'fill-[#D47A4A] text-[#D47A4A]' : 'text-[#E7E5E4]'}`} />
    ))}
  </div>
);

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, hidden: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [lightbox, setLightbox] = useState(null); // {url}

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.listReviews({ status: tab, search, limit: 100 });
      setReviews(data.reviews || []);
      setCounts(data.counts || { pending: 0, approved: 0, hidden: 0 });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tab, search]);

  useEffect(() => {
    const t = setTimeout(fetchAll, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchAll, search]);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await adminAPI.moderateReview(id, action);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta reseña permanentemente?')) return;
    setBusyId(id);
    try {
      await adminAPI.deleteReview(id);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    } finally { setBusyId(null); }
  };

  const tabs = [
    { key: 'pending', label: 'Pendientes', count: counts.pending, className: 'bg-[#D47A4A] text-white' },
    { key: 'approved', label: 'Publicadas', count: counts.approved, className: 'bg-[#2A5C43] text-white' },
    { key: 'hidden', label: 'Ocultas', count: counts.hidden, className: 'bg-[#1C1917] text-white' },
    { key: 'all', label: 'Todas', count: counts.pending + counts.approved + counts.hidden, className: 'bg-[#57534E] text-white' },
  ];

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Panel administrativo</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight" data-testid="admin-reviews-title">Moderación de reseñas</h1>
          <p className="text-[#57534E] mt-2">Aprueba, oculta o elimina las opiniones de los clientes antes de que se publiquen</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="reviews-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                tab === t.key ? t.className : 'bg-white border border-[#E7E5E4] text-[#57534E] hover:border-[#2A5C43]'
              }`}
              data-testid={`reviews-tab-${t.key}`}
            >
              {t.label}
              <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                tab === t.key ? 'bg-white/20' : 'bg-[#F5F5F4]'
              }`}>{t.count}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8BA58F]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por cliente, orden, comentario..."
            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="reviews-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" /></div>
        ) : reviews.length === 0 ? (
          <div className="bg-white rounded-3xl border border-[#E7E5E4] text-center py-16" data-testid="reviews-empty">
            <MessageSquare className="w-12 h-12 text-[#8BA58F] mx-auto mb-4" />
            <p className="text-[#57534E]">No hay reseñas en esta categoría</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="reviews-list">
            {reviews.map((r) => {
              const isPending = !r.is_approved && !r.is_hidden;
              const isApproved = r.is_approved && !r.is_hidden;
              const isHidden = r.is_hidden;
              return (
                <div key={r.id} className="bg-white rounded-2xl border border-[#E7E5E4] p-5" data-testid={`review-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <Stars value={r.rating} />
                        {r.verified_purchase && (
                          <span className="text-[10px] uppercase tracking-widest text-[#2A5C43] font-semibold flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> Compra verificada
                          </span>
                        )}
                        {isPending && <span className="text-[10px] bg-[#FFF7EE] text-[#D47A4A] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest">Pendiente</span>}
                        {isApproved && <span className="text-[10px] bg-[#F7F2E8] text-[#2A5C43] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest">Publicada</span>}
                        {isHidden && <span className="text-[10px] bg-[#F5F5F4] text-[#57534E] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest">Oculta</span>}
                      </div>
                      {r.title && <p className="font-semibold text-[#1C1917]">{r.title}</p>}
                      <p className="text-xs text-[#8BA58F] mt-0.5">
                        {r.customer_name || 'Cliente'} • {r.email} • Pedido {r.order_number} • {fmtDate(r.created_at)}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {!isApproved && (
                        <button
                          onClick={() => act(r.id, 'approve')}
                          disabled={busyId === r.id}
                          className="p-2 hover:bg-[#F7F2E8] rounded-full text-[#2A5C43] disabled:opacity-50"
                          title="Aprobar y publicar"
                          data-testid={`approve-review-${r.id}`}
                        >
                          {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </button>
                      )}
                      {!isHidden && (
                        <button
                          onClick={() => act(r.id, 'hide')}
                          disabled={busyId === r.id}
                          className="p-2 hover:bg-orange-50 rounded-full text-orange-600 disabled:opacity-50"
                          title="Ocultar"
                          data-testid={`hide-review-${r.id}`}
                        >
                          <EyeOff className="w-4 h-4" />
                        </button>
                      )}
                      {isHidden && (
                        <button
                          onClick={() => act(r.id, 'unhide')}
                          disabled={busyId === r.id}
                          className="p-2 hover:bg-[#F7F2E8] rounded-full text-[#2A5C43] disabled:opacity-50"
                          title="Mostrar de nuevo"
                          data-testid={`unhide-review-${r.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => remove(r.id)}
                        disabled={busyId === r.id}
                        className="p-2 hover:bg-red-50 rounded-full text-red-600 disabled:opacity-50"
                        title="Eliminar"
                        data-testid={`delete-review-${r.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {r.comment && (
                    <p className="text-sm text-[#57534E] leading-relaxed bg-[#FAF9F5] rounded-xl p-3 mt-2">{r.comment}</p>
                  )}
                  {r.images && r.images.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[10px] uppercase tracking-widest text-[#8BA58F] font-semibold mb-2 inline-flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" /> Fotos ({r.images.length})
                      </p>
                      <div className="flex flex-wrap gap-2" data-testid={`review-images-${r.id}`}>
                        {r.images.map((img, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setLightbox({ url: img })}
                            className="w-20 h-20 rounded-lg overflow-hidden border border-[#E7E5E4] hover:border-[#2A5C43] transition-colors"
                            data-testid={`review-image-thumb-${r.id}-${idx}`}
                          >
                            <img
                              src={`${BACKEND}${img}?size=400`}
                              alt={`Reseña ${r.id} — foto ${idx + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="review-image-lightbox"
        >
          <button
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
            onClick={() => setLightbox(null)}
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={`${BACKEND}${lightbox.url}?size=1200`}
            alt="Reseña"
            className="max-h-[90vh] max-w-[90vw] rounded-2xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminReviews;
